import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Grid, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import type { MRT_ColumnDef } from "material-react-table"
import { FetchTenantUsersResponse, TenantAdminQuery, fetchTenantUsers } from "../tenantAdminApi"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useApiQueryTable } from "../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { DeleteConfirmDialog } from "../../../../shared/components/dialogs/DeleteConfirmDialog"
import { Button } from "./Button"
import { TENANT_USERS_CREATE_USER_ROUTE, getEditTenantUserRoute } from "../../../../constants/routes"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../core/api/errorTypes"
import { User } from "../../../users/userTypes"
import { UserDetailsDialog } from "./UserDetails/UserDetailsDialog"
import { deleteUser } from "../../../users/userApi"
import { useCurrentTenant } from "../../../tenants/tenantHooks"

function transformData(data: FetchTenantUsersResponse) {
    return data.users
}

const useStyles = makeStyles()(() => ({
    page: {
        paddingTop: "3rem",
    },
    table: {
        marginTop: "3rem",
    },
}))

export function TenantUsersTab() {
    const { classes } = useStyles()

    const [user, setUser] = useState<User>()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)

    const navigate = useNavigate()

    const { l10n } = useAppLocalization()
    const { companyId, userGroupId } = useCurrentTenant()

    const tenantUsers = useApiQuery(fetchTenantUsers, {
        queryName: TenantAdminQuery.USERS_FOR_TENANT,
        dependencies: {
            companyId: companyId,
            userGroupId: userGroupId,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(tenantUsers)

    const columns = useMemo<MRT_ColumnDef<User>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("office-manager-users-table-id-column"),
                accessorKey: "id",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("office-manager-users-table-name-column"),
                accessorKey: "displayName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("office-manager-users-table-email-column"),
                accessorKey: "email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "is-active",
                    header: l10n.getString("office-manager-users-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const { mutateAsync: callDeleteUser } = useApiMutation(deleteUser, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TenantAdminQuery.USERS_FOR_TENANT] })
        },
    })

    function handleDeleteClick() {
        if (user === undefined) {
            return
        } else {
            callDeleteUser({
                userId: user.id,
            })
        }
    }

    return (
        <Grid container>
            <Grid item container xs={12} direction="row" justifyContent="flex-end" mt={3}>
                <LocalizedStrict id="office-manager-users-create-user-button">
                    <Button
                        variant="contained"
                        onClick={() => {
                            navigate(TENANT_USERS_CREATE_USER_ROUTE)
                        }}
                    >
                        Create user
                    </Button>
                </LocalizedStrict>
            </Grid>
            <Grid item xs={12}>
                <ApiQueryTable
                    columns={columns}
                    muiTablePaperProps={{
                        className: classes.table,
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="office-manager-user-show-details">
                            <MenuItem
                                key={row.original.id}
                                onClick={() => {
                                    setUser(row.original)
                                    setShowDetailsModal(true)
                                    closeMenu()
                                }}
                            >
                                Show details
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="office-manager-user-edit">
                            <MenuItem
                                key={row.original.id}
                                onClick={() => {
                                    navigate(getEditTenantUserRoute(row.original.id))
                                    closeMenu()
                                }}
                            >
                                Edit user
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="office-manager-user-delete">
                            <MenuItem
                                key={row.original.id}
                                onClick={() => {
                                    setUser(row.original)
                                    setShowDeleteModal(true)
                                    closeMenu()
                                }}
                            >
                                Delete user
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                />
                {user && (
                    <DeleteConfirmDialog
                        typeOfEntity={l10n.getString("office-manager-user-delete-title")}
                        entityName={user.displayName}
                        handleClick={handleDeleteClick}
                        open={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                    />
                )}

                {user && (
                    <UserDetailsDialog
                        key={user.id}
                        userId={user.id}
                        userType="user"
                        open={showDetailsModal}
                        onClose={() => setShowDetailsModal(false)}
                    />
                )}
            </Grid>
        </Grid>
    )
}
